import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSubordinatesByParent } from '../api/api';
import { User } from '../api/types';
import AdminLayout from '../components/Layout/AdminLayout';
import SubordinatesTable from '../components/SubordinatesTable';
import { useAuth } from '../context/AuthContext';
import { FaUsers, FaFilter, FaUserTie, FaSitemap } from 'react-icons/fa';

const SubordinatesPage: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [subordinates, setSubordinates] = useState<User[]>([]);
  const [admins, setAdmins] = useState<User[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [teamLeaders, setTeamLeaders] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [selectedAdmin, setSelectedAdmin] = useState<User | null>(null);
  const [selectedManager, setSelectedManager] = useState<User | null>(null);
  const [selectedTeamLeader, setSelectedTeamLeader] = useState<User | null>(null);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      setError('');
      try {
        if (currentUser?.role === 'SUPERADMIN') {
          const response = await getSubordinatesByParent(currentUser.id);
          const adminUsers = response.data.filter(u => u.role === 'ADMIN');
          setAdmins(adminUsers);
          setSubordinates(adminUsers); 
        } else {
          const response = await getSubordinatesByParent(null);
          setSubordinates(response.data);
        }
      } catch (err: any) {
        setError('Failed to fetch data. Please check your permissions.');
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [currentUser]);

  const handleSelectAdmin = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = e.target.value;
    if (userId) {
      const admin = admins.find(u => u.id === parseInt(userId));
      setSelectedAdmin(admin || null);
      setSelectedManager(null);
      setSelectedTeamLeader(null);
      setManagers([]);
      setTeamLeaders([]);
      try {
        const response = await getSubordinatesByParent(parseInt(userId));
        const managerUsers = response.data.filter(u => u.role === 'MANAGER');
        setManagers(managerUsers);
        setSubordinates(response.data);
      } catch (err) {
        setError('Failed to fetch manager list.');
      }
    } else {
        setSelectedAdmin(null);
        setSelectedManager(null);
        setSelectedTeamLeader(null);
        setSubordinates(admins);
        setManagers([]);
        setTeamLeaders([]);
    }
  };

  const handleSelectManager = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = e.target.value;
    if (userId) {
      const manager = managers.find(u => u.id === parseInt(userId));
      setSelectedManager(manager || null);
      setSelectedTeamLeader(null);
      setTeamLeaders([]);
      try {
        const response = await getSubordinatesByParent(parseInt(userId));
        const tlUsers = response.data.filter(u => u.role === 'TEAM_LEADER');
        setTeamLeaders(tlUsers);
        setSubordinates(response.data);
      } catch (err) {
        setError('Failed to fetch Team Leader list.');
      }
    } else {
        setSelectedManager(null);
        setSelectedTeamLeader(null);
        setSubordinates(managers);
        setTeamLeaders([]);
    }
  };

  const handleSelectTeamLeader = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const userId = e.target.value;
    if (userId) {
      const tl = teamLeaders.find(u => u.id === parseInt(userId));
      setSelectedTeamLeader(tl || null);
      try {
        const response = await getSubordinatesByParent(parseInt(userId));
        setSubordinates(response.data);
      } catch (err) {
        setError('Failed to fetch Agent list.');
      }
    } else {
        setSelectedTeamLeader(null);
        setSubordinates(teamLeaders);
    }
  };

  const fetchSubordinatesForTable = async () => {
    let parentId = null;
    if (selectedTeamLeader) parentId = selectedTeamLeader.id;
    else if (selectedManager) parentId = selectedManager.id;
    else if (selectedAdmin) parentId = selectedAdmin.id;
    else if (currentUser?.role === 'SUPERADMIN') {
        const response = await getSubordinatesByParent(currentUser.id);
        setSubordinates(response.data);
        return;
    } else {
        const response = await getSubordinatesByParent(null);
        setSubordinates(response.data);
        return;
    }
    try {
        const response = await getSubordinatesByParent(parentId);
        setSubordinates(response.data);
    } catch (err) {
        setError('Failed to refresh data.');
    }
  };

  const selectStyle = "w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm transition-all cursor-pointer";
  const labelStyle = "block text-xs font-bold text-gray-600 dark:text-gray-300 mb-2 uppercase tracking-wide";

  if (loading) return (
    <AdminLayout>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-8 flex items-center justify-center text-gray-600 dark:text-gray-400 font-bold">Loading hierarchy...</div>
    </AdminLayout>
  );

  return (
    <AdminLayout>
      {/* DYNAMIC STYLE BLOCK:
         Ye styles sirf tabhi apply honge jab 'html' tag par 'dark' class hogi.
         Isse aapki table Light mode me default dikhegi aur Dark mode me dark.
      */}
      <style>{`
        .dark .subordinates-dark-wrapper table {
          background-color: #1f2937 !important; /* bg-gray-800 */
          color: #f3f4f6 !important; /* text-gray-100 */
        }
        .dark .subordinates-dark-wrapper thead, 
        .dark .subordinates-dark-wrapper th {
          background-color: #111827 !important; /* bg-gray-900 */
          color: #9ca3af !important; /* text-gray-400 */
          border-bottom: 1px solid #374151 !important; /* border-gray-700 */
        }
        .dark .subordinates-dark-wrapper tbody tr {
          background-color: #1f2937 !important;
          border-bottom: 1px solid #374151 !important;
        }
        .dark .subordinates-dark-wrapper tbody tr:hover {
          background-color: #374151 !important; /* hover:bg-gray-700 */
        }
        .dark .subordinates-dark-wrapper td {
          color: #e5e7eb !important; /* text-gray-200 */
          border-bottom: 1px solid #374151 !important;
        }
      `}</style>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6 md:p-8 font-sans text-gray-900 dark:text-gray-100 transition-colors">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
           <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
             <FaUsers className="mr-3 text-green-600 dark:text-green-500" /> My Subordinates
           </h1>
        </div>

        {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-100 border border-red-200 text-red-700 dark:bg-red-900/50 dark:border-red-700 dark:text-red-200">
                {error}
            </div>
        )}
        
        {/* Filter Section */}
        {currentUser?.role === 'SUPERADMIN' && (
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 mb-8 transition-colors">
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-6 flex items-center border-b border-gray-200 dark:border-gray-700 pb-3">
              <FaSitemap className="mr-2 text-blue-600 dark:text-blue-400" /> Hierarchy Filter
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className={labelStyle}>Select Admin</label>
                <div className="relative">
                    <FaUserTie className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select onChange={handleSelectAdmin} value={selectedAdmin?.id || ''} className={`${selectStyle} pl-10`}>
                    <option value="">All Admins</option>
                    {admins.map(admin => (
                        <option key={admin.id} value={admin.id}>{admin.username}</option>
                    ))}
                    </select>
                </div>
              </div>
              <div className={!selectedAdmin ? "opacity-50 pointer-events-none" : ""}>
                <label className={labelStyle}>Select Manager</label>
                <div className="relative">
                    <FaFilter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select onChange={handleSelectManager} value={selectedManager?.id || ''} className={`${selectStyle} pl-10`}>
                    <option value="">All Managers</option>
                    {managers.map(manager => (
                        <option key={manager.id} value={manager.id}>{manager.username}</option>
                    ))}
                    </select>
                </div>
              </div>
              <div className={!selectedManager ? "opacity-50 pointer-events-none" : ""}>
                <label className={labelStyle}>Select Team Leader</label>
                <div className="relative">
                    <FaUsers className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <select onChange={handleSelectTeamLeader} value={selectedTeamLeader?.id || ''} className={`${selectStyle} pl-10`}>
                    <option value="">All Team Leaders</option>
                    {teamLeaders.map(tl => (
                        <option key={tl.id} value={tl.id}>{tl.username}</option>
                    ))}
                    </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Table Section with CSS Wrapper */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-1 overflow-hidden subordinates-dark-wrapper transition-colors">
            {subordinates.length > 0 ? (
            <div className="overflow-x-auto">
                <SubordinatesTable
                    subordinates={subordinates}
                    onActionComplete={fetchSubordinatesForTable}
                />
            </div>
            ) : (
            <div className="p-10 text-center flex flex-col items-center justify-center text-gray-500 dark:text-gray-400">
                <FaUsers size={48} className="mb-4 opacity-20" />
                <p>No subordinates found for the selected criteria.</p>
            </div>
            )}
        </div>

      </div>
    </AdminLayout>
  );
};

export default SubordinatesPage;